import React from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  * { margin:0; padding:0; box-sizing:border-box; }

  body {
    background:#07070a;
    font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
    color:#fff;
  }

  @keyframes fadeUp {
    from { opacity:0; transform:translateY(25px); }
    to { opacity:1; transform:translateY(0); }
  }

  @keyframes pulseGlow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(168,85,247,0.5), 0 0 20px rgba(168,85,247,0.3); }
    50% { box-shadow: 0 0 0 8px rgba(168,85,247,0), 0 0 40px rgba(168,85,247,0.5); }
  }

  @keyframes shimmer {
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
  }

  .btn-animated {
    background: linear-gradient(135deg, #6d28d9, #a855f7, #7c3aed, #a855f7);
    background-size: 300% 100%;
    color:#fff;
    border: none;
    padding:15px 32px;
    border-radius:999px;
    cursor:pointer;
    font-weight:700;
    font-size:15px;
    display:inline-flex;
    align-items:center;
    gap:10px;
    font-family: "Inter", sans-serif;
    animation: shimmer 3s linear infinite, pulseGlow 2.5s ease-in-out infinite;
    transition: transform 0.2s;
    letter-spacing: 0.01em;
  }
  .btn-animated:hover { transform: scale(1.05); }

  .btn-ghost {
    background: transparent;
    border:1px solid rgba(255,255,255,0.85);
    color:#fff;
    padding:14px 26px;
    border-radius:999px;
    cursor:pointer;
    font-weight:500;
    display:inline-flex;
    align-items:center;
    gap:8px;
    font-family: "Inter", sans-serif;
    transition: background 0.2s;
  }
  .btn-ghost:hover { background: rgba(255,255,255,0.07); }

  .tag {
    background: rgba(168,85,247,0.15);
    color:#a855f7;
    padding:6px 14px;
    font-size:11px;
    border-radius:999px;
    text-transform:uppercase;
    letter-spacing:0.1em;
    display:inline-block;
    border:1px solid rgba(168,85,247,0.3);
  }

  .hero {
    position:relative; height:100vh; overflow:hidden;
    display:flex; align-items:center; justify-content:center; text-align:center;
  }
  .hero video { position:absolute; top:0; left:0; width:100%; height:100%; object-fit:cover; z-index:0; }
  .overlay { position:absolute; inset:0; background:rgba(0,0,0,0.65); z-index:1; }
  .hero-content { position:relative; z-index:2; max-width:800px; padding:20px; animation:fadeUp 1s ease; }

  .divider { height:1px; background:rgba(255,255,255,0.08); }

  .info-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(260px,1fr)); gap:20px; margin-top:30px; }
  .info-card { background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.09); border-radius:20px; padding:28px 24px; transition:border-color 0.3s,transform 0.3s; }
  .info-card:hover { border-color:rgba(168,85,247,0.4); transform:translateY(-4px); }
  .info-card .icon-wrap { width:48px;height:48px;border-radius:14px;background:rgba(168,85,247,0.12);border:1px solid rgba(168,85,247,0.25);display:flex;align-items:center;justify-content:center;font-size:22px;margin-bottom:16px; }
  .info-card h3 { font-size:17px;font-weight:700;margin-bottom:6px;color:#fff; }
  .info-card .sub { font-size:12px;color:rgba(168,85,247,0.9);text-transform:uppercase;letter-spacing:0.08em;margin-bottom:16px;font-weight:600; }
  .info-card ul { list-style:none;display:flex;flex-direction:column;gap:9px; }
  .info-card ul li { font-size:13.5px;color:rgba(255,255,255,0.65);display:flex;align-items:center;gap:8px; }
  .info-card ul li::before { content:'';width:5px;height:5px;border-radius:50%;background:#a855f7;flex-shrink:0; }

  .flow-wrap { display:flex;align-items:center;justify-content:center;flex-wrap:wrap;gap:0;margin-top:40px; }
  .flow-step { display:flex;flex-direction:column;align-items:center;gap:10px;text-align:center; }
  .flow-icon { width:64px;height:64px;border-radius:18px;background:rgba(168,85,247,0.1);border:1px solid rgba(168,85,247,0.3);display:flex;align-items:center;justify-content:center;font-size:26px; }
  .flow-label { font-size:12px;font-weight:600;color:rgba(255,255,255,0.75);max-width:80px;line-height:1.4; }
  .flow-arrow { font-size:20px;color:rgba(168,85,247,0.6);margin:0 6px;padding-bottom:24px; }

  .step-card { background:rgba(255,255,255,0.03);border:1px solid rgba(255,255,255,0.18);border-radius:16px;padding:20px 24px;display:flex;align-items:center;gap:18px; }
  .step-num { font-size:13px;font-weight:800;color:#a855f7;background:rgba(168,85,247,0.1);border:1px solid rgba(168,85,247,0.3);border-radius:10px;padding:6px 12px;flex-shrink:0; }
`;

const Arrow = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M2 7h10M7 2l5 5-5 5" stroke="white" strokeWidth="1.5"/>
  </svg>
);

const infoSections = [
  { icon:"💰", sub:"What You Get Inside", title:"Inside The Private System", items:["Premium commercial contracts","High-value job access","Consistent bid opportunities","Private platform access","Scalable revenue flow"] },
  { icon:"🏗️", sub:"Who This Is Built For", title:"For Growing Junk Businesses", items:["Startup operators","Small fleets","Growing teams","Commercial contractors","Service-based companies"] },
  { icon:"📈", sub:"Scale Your Business", title:"Move Beyond Local Jobs", items:["Increase monthly revenue","Access bigger contracts","Reduce downtime gaps","Build predictable income","Grow into enterprise level"] },
  { icon:"🚀", sub:"Why This System Exists", title:"Simple Core Mission", items:["Connect businesses to contracts","Replace inconsistent lead flow","Enable competitive bidding","Help scale operations faster","Build predictable growth systems"] },
];

const flowSteps = [
  { icon:"📦", label:"Contract Source" },
  { icon:"🌐", label:"Private Bidding System" },
  { icon:"📝", label:"You Place Bid" },
  { icon:"🏆", label:"Contract Awarded" },
  { icon:"🚛", label:"Job Completion" },
  { icon:"💰", label:"Revenue Growth" },
];

export default function JunkBid() {
  const navigate = useNavigate();

  const scrollToHowItWorks = () => {
    document.getElementById("how-it-works")?.scrollIntoView({ behavior:"smooth" });
  };

  return (
    <>
      <style>{css}</style>
      <Helmet><title>Private Junk Contracts | JunkBid</title></Helmet>
      <div>

        <section className="hero">
          <video autoPlay muted loop playsInline>
            <source src="https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/videos/junk%20removal.mp4" type="video/mp4" />
          </video>
          <div className="overlay"></div>
          <div className="hero-content">
            <span className="tag">Limited Access Network</span>
            <h1 style={{ fontSize:"72px", marginTop:16, fontWeight:900, lineHeight:1.05, textAlign:"center", letterSpacing:"-1px" }}>
              <span style={{ color:"#a855f7" }}>Exclusive Contract</span>{" "}
              <span style={{ color:"#ffffff" }}>Scaling Access</span>
            </h1>
            <p style={{ marginTop:16, color:"#ffffff", maxWidth:520, marginInline:"auto" }}>
              Private Job Bidding Network
            </p>
            <div style={{ marginTop:28, display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap" }}>
              <button className="btn-animated" onClick={() => navigate("/private-access-form")}>Request Access <Arrow /></button>
              <button className="btn-ghost" onClick={scrollToHowItWorks}>How it works</button>
            </div>
          </div>
        </section>

        <div className="divider" />

        <section id="how-it-works" style={{ padding:"80px 20px", maxWidth:900, margin:"0 auto" }}>
          <span className="tag">How it works</span>
          <h2 style={{ fontSize:32, fontWeight:800, marginTop:14, color:"#fff" }}>Private contract allocation system</h2>
          <div style={{ display:"grid", gap:14, marginTop:30 }}>
            {[["01","Customer submits job request"],["02","We assign verified companies"],["03","You place your bid"],["04","Best bid wins contract"]].map((x,i) => (
              <div key={i} className="step-card">
                <span className="step-num">{x[0]}</span>
                <span style={{ fontSize:15, fontWeight:500, color:"#fff" }}>{x[1]}</span>
              </div>
            ))}
          </div>
        </section>

        <div className="divider" />

        <section style={{ padding:"80px 20px", maxWidth:1000, margin:"0 auto", textAlign:"center" }}>
          <span className="tag">Platform Flow</span>
          <h2 style={{ fontSize:32, fontWeight:800, marginTop:14, color:"#fff" }}>How The Platform Works</h2>
          <p style={{ marginTop:10, color:"rgba(255,255,255,0.5)", fontSize:14 }}>From contract source to revenue — every step in one system.</p>
          <div className="flow-wrap">
            {flowSteps.map((step, i) => (
              <React.Fragment key={i}>
                <div className="flow-step">
                  <div className="flow-icon">{step.icon}</div>
                  <div className="flow-label">{step.label}</div>
                </div>
                {i < flowSteps.length - 1 && <div className="flow-arrow">→</div>}
              </React.Fragment>
            ))}
          </div>
        </section>

        <div className="divider" />

        <section style={{ padding:"80px 20px", maxWidth:1100, margin:"0 auto" }}>
          <span className="tag">Inside the system</span>
          <h2 style={{ fontSize:32, fontWeight:800, marginTop:14, color:"#fff" }}>Everything you need to scale</h2>
          <p style={{ marginTop:10, color:"rgba(255,255,255,0.5)", fontSize:14, maxWidth:500 }}>Built for junk removal companies ready to grow beyond local markets.</p>
          <div className="info-grid">
            {infoSections.map((sec, i) => (
              <div key={i} className="info-card">
                <div className="icon-wrap">{sec.icon}</div>
                <div className="sub">{sec.sub}</div>
                <h3>{sec.title}</h3>
                <ul>{sec.items.map((item, j) => <li key={j}>{item}</li>)}</ul>
              </div>
            ))}
          </div>
        </section>

        <div className="divider" />

        <section style={{ padding:"80px 20px 120px", textAlign:"center" }}>
          <span className="tag">Get started</span>
          <h2 style={{ fontSize:40, fontWeight:900, marginTop:16, letterSpacing:"-0.5px" }}>Ready to scale your business?</h2>
          <p style={{ marginTop:12, color:"rgba(255,255,255,0.5)", fontSize:15 }}>Join the private network and start bidding on premium contracts today.</p>
          <div style={{ marginTop:28 }}>
            <button className="btn-animated" style={{ fontSize:15, padding:"16px 36px" }} onClick={() => navigate("/private-access-form")}>
              Request Access <Arrow />
            </button>
          </div>
        </section>

      </div>
    </>
  );
}